import { Typography } from 'beek-design-system';
import { useState } from 'react';
import styled from 'styled-components';

import iconPath from './icons-library';

type IconProps = {
  name: string;
  onClick?: () => void;
  width?: string;
  height?: string;
  marginTop?: string;
  marginBottom?: string;
  marginLeft?: string;
  marginRight?: string;
  colorVariant?: string;
  color?: string;
  url?: string;
  external?: boolean;
  feedbackLabel?: string;
  iconState?: boolean;
  hover?: boolean;
};

type PathProps = {
  colorVariant?: string;
  color?: string;
  hover?: boolean;
};

type AlertProps = {
  fadeOut: boolean;
};

const defaultProps = {
  marginTop: '0rem',
  marginBottom: '0rem',
  marginLeft: '0rem',
  marginRight: '0rem',
  colorVariant: 'gray100',
  color: null,
  width: null,
  height: null,
  onClick: (): void => {},
  url: null,
  external: false,
  feedbackLabel: null,
  iconState: false,
  hover: false,
};

interface Dictionary {
  [index: string]: string;
}

const getColor = (
  colorVariant: string,
  colors: Dictionary,
  color?: string
): string => {
  if (color) return color;
  return colors[colorVariant];
};

const getSize = (type: string, name: string, size?: string): string => {
  if (size) {
    return size;
  }
  if (type === 'width') {
    return iconPath[name].width;
  }
  return iconPath[name].height;
};

/* eslint-disable @typescript-eslint/indent */
const Svg = styled.svg<IconProps>`
  width: ${(props): string => getSize('width', props.name, props.width)};
  height: ${(props): string => getSize('height', props.name, props.height)};
  fill: none;
  margin-top: ${(props): string | undefined => props.marginTop};
  margin-bottom: ${(props): string | undefined => props.marginBottom};
  margin-left: ${(props): string | undefined => props.marginLeft};
  margin-right: ${(props): string | undefined => props.marginRight};
  cursor: pointer;
  @media ${({ theme }): string => theme.devices.laptop} {
    &:hover {
      path {
        fill: ${(props): string =>
          props.iconState
            ? 'var(--bright-blue)'
            : getColor(
                props.colorVariant ? props.colorVariant : 'gray100',
                props.theme.colors,
                props.color
              )};
      }
    }
    &:active {
      path {
        fill: ${(props): string =>
          props.iconState
            ? 'var(--gray-70)'
            : getColor(
                props.colorVariant ? props.colorVariant : 'gray100',
                props.theme.colors,
                props.color
              )};
      }
    }
    &:visited {
      path {
        fill: ${(props): string =>
          props.iconState
            ? 'var(--medium-blue)'
            : getColor(
                props.colorVariant ? props.colorVariant : 'gray100',
                props.theme.colors,
                props.color
              )};
      }
    }
  }
`;

const Path = styled.path<PathProps>`
  fill: ${(props): string =>
    getColor(
      props.colorVariant ? props.colorVariant : 'gray100',
      props.theme.colors,
      props.color
    )};
  @media ${({ theme }): string => theme.devices.laptop} {
    fill: ${(props): string =>
      props.hover
        ? 'var(--gray-50)'
        : getColor(
            props.colorVariant ? props.colorVariant : 'gray100',
            props.theme.colors,
            props.color
          )};
  }
  fill-rule: evenodd;
  clip-rule: evenodd;
`;
/* eslint-enable @typescript-eslint/indent */

const Alert = styled.div<AlertProps>`
  margin-top: -2.813rem;
  margin-left: -2.5rem;
  position: absolute;
  background-color: var(--gray-100);
  color: var(--white);
  border-radius: 6.25rem;
  width: 6.938rem;
  height: 2rem;
  padding: 0.25rem 1rem;
  opacity: ${(props): string => (props.fadeOut ? '1' : '0')};
  -webkit-transition: opacity 1.5s;
  -moz-transition: opacity 1.5s;
  transition: opacity 1.5s;
  @media ${({ theme }): string => theme.devices.laptop} {
    width: 8.25rem;
    height: 2.25rem;
    padding: 0.5rem 1.656rem;
    margin-left: -3.438rem;
  }
`;

const FlexColumn = styled.div`
  display: flex;
  flex-direction: column;
`;

const Icon = (props: IconProps): JSX.Element => {
  const {
    name,
    width,
    height,
    onClick,
    marginTop,
    marginBottom,
    marginRight,
    marginLeft,
    color,
    colorVariant,
    url,
    external,
    feedbackLabel,
    hover,
  } = props;
  const [fadeOut, setFadeOut] = useState(false);
  const [showAlert, setShowAlert] = useState(false);
  const { viewBox } = iconPath[name];
  const d = iconPath[name] ? iconPath[name].path : '';
  let iconState = false;
  if (url || name === 'clipboard') {
    iconState = true;
  }

  const styleProps = {
    marginTop,
    marginBottom,
    marginRight,
    marginLeft,
    width,
    height,
    name,
    viewBox,
    xmlns: 'http://www.w3.org/2000/svg',
    color,
    colorVariant,
    iconState,
  };

  const pathProps = {
    d,
    color,
    colorVariant,
    hover,
  };

  const handleFadeEffect = (): void => {
    setTimeout(() => {
      setFadeOut(false);
    }, 1000);
    setTimeout(() => {
      setShowAlert(false);
    }, 2000);
    setShowAlert(true);
    setFadeOut(true);
  };

  const handleOnClick = (): void => {
    if (onClick) onClick();
    if (feedbackLabel) handleFadeEffect();
  };

  if (iconPath[name]) {
    if (name === 'clipboard') {
      return (
        <FlexColumn>
          {showAlert && feedbackLabel && (
            <Alert fadeOut={fadeOut}>
              <Typography type="body2" colorVariant="white">
                {feedbackLabel}
              </Typography>
            </Alert>
          )}
          <Svg {...styleProps} onClick={(): void => handleOnClick()}>
            <Path {...pathProps} />
            <Path
              d={iconPath.clipboard2.path}
              color={color}
              colorVariant={colorVariant}
            />
          </Svg>
        </FlexColumn>
      );
    }
    if (
      name === 'user_icon_secondary' ||
      name === 'question' ||
      name === 'pause' ||
      name === 'settings' ||
      name === 'logout' ||
      name === 'eyeOpen' ||
      name === 'edit' ||
      name === 'help'
    ) {
      const index = `${name}2`;
      return (
        <FlexColumn>
          <Svg {...styleProps} onClick={(): void => handleOnClick()}>
            <Path {...pathProps} />
            <Path
              d={iconPath[index].path}
              color={color}
              colorVariant={colorVariant}
            />
          </Svg>
        </FlexColumn>
      );
    }
    if (url && !external) {
      return (
        <a href={url} target="_self" rel="noreferrer" data-testid="iconUrl">
          <Svg {...styleProps}>
            <Path {...pathProps} />
          </Svg>
        </a>
      );
    }
    if (url && external) {
      return (
        <a
          href={url}
          target="_blank"
          rel="noreferrer"
          data-testid="iconUrlExternal"
        >
          <Svg {...styleProps}>
            <Path {...pathProps} />
          </Svg>
        </a>
      );
    }
    return (
      <FlexColumn data-testid="icon">
        {showAlert && feedbackLabel && (
          <Alert fadeOut={fadeOut}>
            <Typography type="body2" colorVariant="white">
              {feedbackLabel}
            </Typography>
          </Alert>
        )}
        <Svg {...styleProps} onClick={(): void => handleOnClick()}>
          <Path {...pathProps} />
        </Svg>
      </FlexColumn>
    );
  }
  return <></>; // eslint-disable-line react/jsx-no-useless-fragment
};

Icon.defaultProps = defaultProps;

export default Icon;
